/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row, Col, Spin, Modal, Button,
} from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError } from 'utils/handlers';
import { checkToken } from 'api/utils';
import { ESTATUS_CANCELADO } from 'utils/constants';
import { getEvaluacionGeneralReloj } from 'api/configuraciones/clock/EvaluacionClock/EvaluacionGeneral';
import {
  getBonificacionesEmpleados,
  patchBonificacionEmpleado,
} from 'api/nominas/bonificaciones/bonificacionesEmpleados';

// Mismo estilo de "campo de detalle" que Detalle de Justificación Grupal
// (ver JustificacionGrupalGeneral.jsx): label gris pequeño arriba, valor
// dentro de una caja con borde.
const CampoDetalle = ({
  label, value, span = 8, full = false,
}) => (
  <Col xs={24} sm={span} md={span} style={{ marginBottom: 12 }}>
    <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 4 }}>{label}</div>
    <div style={{
      border: '1px solid #d9d9d9',
      borderRadius: 4,
      padding: '4px 11px',
      minHeight: 32,
      whiteSpace: full ? 'pre-line' : undefined,
    }}
    >
      {value || '-'}
    </div>
  </Col>
);

/**
 * Aplana una BonificacionEmpleado a un registro plano para la tabla, ya que
 * Table (components/Table) resuelve cada columna con `record[dataIndex]`
 * (sin soporte de rutas anidadas).
 */
const flattenBonificacionEmpleado = (registros) => {
  const filas = (registros || []).map((registro) => ({
    id: registro.id,
    empleado_numero: registro.empleado?.numero_de_empleado || '',
    empleado_numero_y_nombre_completo: registro.empleado?.numero_y_nombre_completo || '',
    directorio_de_area_clave_y_nombre: registro.empleado?.directorio_de_area_clave_y_nombre || '',
    puesto_clave_y_descripcion: registro.empleado?.puesto_clave_y_descripcion || '',
    plaza_clave_y_descripcion: registro.empleado?.plaza_clave_y_descripcion || '',
    descuento_anterior: registro.descuento_empleado
      ? `${registro.descuento_empleado.clave || `#${registro.descuento_empleado.id}`} - ${registro.descuento_empleado.concepto || ''}`
      : '',
    // Días originalmente descontados (y su periodo) del Descuento Empleado
    // que esta Bonificación reembolsa -- se usan solo en el detalle (doble
    // clic), no como columna de la tabla.
    dias_descontados_original: registro.descuento_empleado?.dias_descontados ?? null,
    descuento_periodo: registro.descuento_empleado?.fecha_inicial
      ? `${registro.descuento_empleado.fecha_inicial} al ${registro.descuento_empleado.fecha_final}`
      : '',
    // Fechas exactas (día por día) de la incidencia de Reloj Checador que
    // originó el descuento -- ver nota en el backend (obtener_fechas_de_incidencia)
    // sobre por qué esto es informativo para conceptos con umbrales.
    fechas_incidencia: registro.fechas_incidencia?.length
      ? registro.fechas_incidencia.join('\n')
      : '',
    dias_bonificados: registro.dias_bonificados ?? '',
    importe: registro.importe ?? '',
    periodo_de_nomina_detalle: registro.periodo_de_nomina_detalle
      ? `${registro.periodo_de_nomina_detalle.numero_de_nomina} (${registro.periodo_de_nomina_detalle.fecha_inicial} al ${registro.periodo_de_nomina_detalle.fecha_final})`
      : '',
    observaciones: registro.observaciones || '',
    estatus: registro.estados_globales?.descripcion || '',
    estados_globales_id: registro.estados_globales?.id,
    // Una Bonificación ya Cancelada no se puede volver a cancelar.
    ya_cancelada: registro.estados_globales?.id === ESTATUS_CANCELADO,
  }));
  // Activas primero, Canceladas al final. `Array.prototype.sort` es estable,
  // así que dentro de cada grupo se conserva el orden que ya traía el backend.
  return [...filas].sort((a, b) => Number(a.ya_cancelada) - Number(b.ya_cancelada));
};

const IncidenciaBonificaciones = ({ permission, parametroBusqueda, activeTabKey }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [detalleRecord, setDetalleRecord] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const evaluacion = await getEvaluacionGeneralReloj(parametroBusqueda);
      const evaluacionId = evaluacion?.results ? evaluacion.results[0]?.id : null;
      if (!evaluacionId) {
        setData([]);
        return;
      }
      const _data = await getBonificacionesEmpleados({
        guardar_incidencia_evaluacion_cierre: evaluacionId,
      });
      setData(flattenBonificacionEmpleado(_data?.results || _data));
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  // Este tab convive con las demás pestañas de Incidencias vía `forceRender`
  // (todas se montan de inmediato, ocultas con CSS), así que se refresca
  // cada vez que el usuario entra a esta pestaña para reflejar las
  // bonificaciones recién creadas en el tab "Descuentos".
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      if (activeTabKey !== 'Bonificaciones') {
        return;
      }
      setLoading(true);
      setSelectedRowKeys([]);
      await checkToken();
      if (mounted) {
        await fetchData();
      }
      setLoading(false);
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [activeTabKey]);

  // Selección de filas para Cancelar: mismo patrón que IncidenciaDescuentos.jsx
  // (la casilla de selección no es clickeable por una regla CSS global -- la
  // selección real es por clic en la fila, ver `handleOnRowClick`). No se
  // puede seleccionar una Bonificación ya Cancelada.
  const handleOnRowClick = (record) => {
    if (record.ya_cancelada) {
      return;
    }
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id));
    } else {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    getCheckboxProps: (record) => ({
      disabled: record.ya_cancelada,
    }),
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      // Cada patch ya reporta sus propios éxitos/errores (ver
      // `patchBonificacionEmpleado`); se refresca siempre al final para
      // reflejar el resultado real, aunque alguno haya fallado.
      await Promise.all(selectedRowKeys.map(
        (id) => patchBonificacionEmpleado(id, { estados_globales: ESTATUS_CANCELADO }),
      ));
      setVisibleCancelar(false);
      setSelectedRowKeys([]);
      await fetchData();
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado_numero',
      key: 'empleado_numero',
      width: 150,
    },
    {
      titleText: 'Nombre del Empleado',
      dataIndex: 'empleado_numero_y_nombre_completo',
      key: 'empleado_numero_y_nombre_completo',
      width: 280,
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area_clave_y_nombre',
      key: 'directorio_de_area_clave_y_nombre',
      width: 300,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_clave_y_descripcion',
      key: 'puesto_clave_y_descripcion',
      width: 260,
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plaza_clave_y_descripcion',
      key: 'plaza_clave_y_descripcion',
      width: 260,
    },
    {
      titleText: 'Descuento Anterior',
      dataIndex: 'descuento_anterior',
      key: 'descuento_anterior',
      width: 260,
    },
    {
      titleText: 'Días Bonificados',
      dataIndex: 'dias_bonificados',
      key: 'dias_bonificados',
      width: 170,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 160,
    },
    {
      titleText: 'Periodo de Nómina Detalle',
      dataIndex: 'periodo_de_nomina_detalle',
      key: 'periodo_de_nomina_detalle',
      width: 260,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 220,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus',
      key: 'estatus',
      width: 160,
    },
  ];

  return (
    <Row>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          allowDoubleClick
          controls={{ onClickExpand: (record) => setDetalleRecord(record) }}
          customActions={[
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: 'bonificacionempleado', action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              className: 'cancel',
            },
          ]}
          permission={permission}
          allowExportPDF
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          Confirm
          onCancel={() => setVisibleCancelar(false)}
          contentTitle="Cancelar Bonificación"
          open={visibleCancelar}
          loading={loading}
        />
        <Modal
          title={`Detalle de Bonificación${detalleRecord ? ` - ${detalleRecord.empleado_numero_y_nombre_completo}` : ''}`}
          open={!!detalleRecord}
          onCancel={() => setDetalleRecord(null)}
          footer={[
            <Button key="cerrar" onClick={() => setDetalleRecord(null)}>Cerrar</Button>,
          ]}
          width="80%"
          centered
          bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        >
          {detalleRecord && (
            <Row gutter={10}>
              <CampoDetalle label="No. de Empleado" value={detalleRecord.empleado_numero} />
              <CampoDetalle label="Nombre del Empleado" value={detalleRecord.empleado_numero_y_nombre_completo} />
              <CampoDetalle label="Área" value={detalleRecord.directorio_de_area_clave_y_nombre} />
              <CampoDetalle label="Puesto" value={detalleRecord.puesto_clave_y_descripcion} />
              <CampoDetalle label="Plaza" value={detalleRecord.plaza_clave_y_descripcion} />
              <CampoDetalle label="Estatus" value={detalleRecord.estatus} />
              <CampoDetalle label="Descuento Anterior" value={detalleRecord.descuento_anterior} span={12} />
              <CampoDetalle label="Periodo del Descuento" value={detalleRecord.descuento_periodo} />
              <CampoDetalle label="Días Descontados (Original)" value={detalleRecord.dias_descontados_original} />
              <CampoDetalle label="Días Bonificados" value={detalleRecord.dias_bonificados} />
              <CampoDetalle label="Importe" value={detalleRecord.importe} />
              <CampoDetalle label="Periodo de Nómina Detalle" value={detalleRecord.periodo_de_nomina_detalle} span={12} />
              <CampoDetalle
                label="Fechas Exactas de la Incidencia"
                value={detalleRecord.fechas_incidencia}
                span={12}
                full
              />
              <CampoDetalle label="Observaciones" value={detalleRecord.observaciones} span={16} />
            </Row>
          )}
        </Modal>
      </Spin>
    </Row>
  );
};

export default IncidenciaBonificaciones;
